// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@tps/App/Components/Common/XngZone";
import { CustomAppState, xProductListActions } from "@tps/App/Store/Reducers";
import { FormFieldStyle } from "@tps/MobiusOverrides/FormField";
import { AllProductPageContexts, DivWrapper } from "@tps/XDL/constants";
import Close from "@tps/XDL/Icons/Close";
import { color, hideElement, styledScrollbar, xScreen } from "@tps/XDL/Utilities";
import React, { Dispatch, FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    return {
        isProductListFilterModalOpen: state.xProductList.isProductListFilterModalOpen,
    };
};

const mapDispatchToProps = {
    clearAllProductFilters,
    setFiltersModalOpen: (isOpen: boolean) => (dispatch: Dispatch<xProductListActions>) => {
        dispatch({ type: "xProductList/isProductListFilterModalOpen", isOpen });
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListLayoutStyles {
    container?: InjectableCss;
    leftColumnContainer?: InjectableCss;
    rightColumnContainer?: InjectableCss;
    headerWrapper?: InjectableCss;
    categoryWrapper?: InjectableCss;
    sortAndPaginationWrapper?: InjectableCss;
    modal?: ModalPresentationProps;
    productListTitleWrapper?: InjectableCss;
    filterInnerWrapper?: GridItemProps;
    filterButtonsWrapper?: InjectableCss;
    paginationBottomSection?: InjectableCss;
    clearFilters?: LinkPresentationProps;
}

export const layoutStyles: ProductListLayoutStyles = {
    container: {
        css: css`
            display: flex;
            gap: 42px;
        `,
    },
    leftColumnContainer: {
        css: css`
            flex: 0 0 297px;
            ${xScreen("lte", "md")} {
                ${hideElement}
            }
        `,
    },
    rightColumnContainer: {
        css: css`
            flex-grow: 1;
            display: flex;
            flex-direction: column;
        `,
    },
    headerWrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 20px;

            & > ${DivWrapper} {
                &:empty,
                &:has(> div:empty),
                & > ${DivWrapper}:has(> div:empty) {
                    display: none;
                }
            }
        `,
    },
    categoryWrapper: {
        css: css`
            & > ${DivWrapper} {
                display: flex;
                flex-direction: column;
                gap: 24px;
                & > [data-test-selector="categoryImagePath"] {
                    margin-bottom: 8px;
                }

                &:empty {
                    display: none;
                }
            }

            &:has(> div:empty) {
                display: none;
            }
        `,
    },
    sortAndPaginationWrapper: {
        css: css`
            display: flex;
            width: 100%;
            gap: 16px;
            align-items: center;
            justify-content: space-between;
            & > div {
                margin: 0;
                flex-basis: 50%;
                & > span {
                    margin: 0;
                }
            }
            ${FormFieldStyle} {
                flex-direction: column;
                align-items: start;
                gap: 8px;
                & > div {
                    margin: 0;
                    width: 100%;
                }
            }
        `,
    },
    productListTitleWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            flex-wrap: wrap;
            width: 100%;
            gap: 12px 32px;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
                align-items: flex-start;
            }

            h2 {
                font-size: 28px;
                line-height: 36px;
                margin: 0;
            }

            & > div:empty {
                display: none;
            }
        `,
    },
    modal: {
        cssOverrides: {
            modalContainer: css`
                width: 360px;
                max-width: 100%;
                ${xScreen("lte", "xs")} {
                    width: 100%;
                }
            `,
            modalTitle: css`
                padding: 16px;
                &:after {
                    display: none;
                }
                ${TypographyStyle} {
                    font-size: 16px;
                    line-height: 22px;
                }
            `,
            modalContent: css`
                padding: 0 16px;
                height: 100%;
                overflow-y: auto;
                ${styledScrollbar(4)}
                > div {
                    height: 100%;
                }
            `,
            modalBody: css`
                ${xScreen("lte", "md")} {
                    height: 100%;
                    max-height: 100%;
                    max-width: 100%;
                    margin: 0;
                }
            `,
        },
        closeButtonIconProps: {
            src: Close,
            size: 13,
            color: "text",
        },
        closeButtonProps: {
            css: css`
                width: 13px;
                height: 13px;
                min-height: 13px;
                padding: 0;
                &,
                &:hover,
                &:focus {
                    background: transparent;
                    border: 0;
                }
                &:hover {
                    ${IconWrapper} {
                        color: ${color("primary")};
                    }
                }
            `,
        },
    },
    filterInnerWrapper: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    filterButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            margin-block: 20px 10px;
        `,
    },
    paginationBottomSection: {
        css: css`
            display: flex;
            justify-content: flex-end;
            background: ${color("common.ui2")};
            padding-inline: 12px;
        `,
    },
    clearFilters: {
        typographyProps: {
            size: 14,
            css: css`
                && {
                    line-height: 22px;
                }
            `,
        },
    },
};

const styles = layoutStyles;

const ProductListLayout: FC<Props> = ({
    id,
    isProductListFilterModalOpen,
    clearAllProductFilters,
    setFiltersModalOpen,
}) => {
    const modalCloseHandler = () => {
        setFiltersModalOpen(false);
    };

    const clearFiltersHandler = () => {
        clearAllProductFilters();
    };

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.leftColumnContainer}>
                <XngZone contentId={id} zoneName="ContentFilter" hint="Filter Section" />
            </StyledWrapper>
            <StyledWrapper {...styles.rightColumnContainer}>
                <StyledWrapper {...styles.headerWrapper} data-test-selector="header-productList">
                    <StyledWrapper {...styles.productListTitleWrapper}>
                        <XngZone contentId={id} zoneName="ContentMainHeader00" hint="Title/Query" normalWidth />
                        <XngZone contentId={id} zoneName="ContentMainHeader02" hint="Products Count" normalWidth />
                    </StyledWrapper>
                    <XngZone contentId={id} zoneName="ContentMainHeader06" hint="Suggestion" />
                    <XngZone contentId={id} zoneName="ContentMainHeader03" hint="ProductList Controls Container" />
                </StyledWrapper>
                <XngZone contentId={id} zoneName="ContentMainSection" hint="Main Section" />
                <StyledWrapper {...styles.paginationBottomSection}>
                    <XngZone
                        contentId={id}
                        zoneName="paginationBottomSection"
                        normalWidth={true}
                        hint="Pagination Page"
                    />
                </StyledWrapper>
            </StyledWrapper>
            <Modal
                {...styles.modal}
                headline={translate("Refine Results")}
                isOpen={isProductListFilterModalOpen}
                handleClose={modalCloseHandler}
            >
                <GridContainer gap={20}>
                    <GridItem width={12}>
                        <StyledWrapper {...styles.sortAndPaginationWrapper}>
                            <XngZone contentId={id} zoneName="ContentMainHeader01" hint="Sort Item" />
                            {createWidgetElement("ProductList/ProductListPagination", {
                                fields: {
                                    showPagesNumber: false,
                                    showResultsPerPage: true,
                                },
                            })}
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.filterInnerWrapper}>
                        <XngZone contentId={id} zoneName="ContentFilter" hint="Filter Section" />
                        <StyledWrapper {...styles.filterButtonsWrapper}>
                            <Link {...styles.clearFilters} onClick={clearFiltersHandler}>
                                {translate("Clear Filters")}
                            </Link>
                            <Button variant="primary" onClick={modalCloseHandler}>
                                {translate("Apply Filters")}
                            </Button>
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListLayout),
    definition: {
        group: "Product List",
        displayName: "ProductList Layout",
        // xng-change
        allowedContexts: ["ProductListPage", ...AllProductPageContexts],
    },
};

export default widgetModule;
